'use client';

import { useState } from 'react';
import { VStack } from '@terra/ui-styled-system';
import { useTranslations } from 'next-intl';
import type {
  ExternalNetworkDto,
  UpdateExternalNetworkDto,
} from '@terra/shared-contracts-external-network';
import { Field } from '../../../components/field';
import { Input } from '../../../components/input';
import { SettingsBooleanControl } from '../../settings/SettingsBooleanControl';
import { SettingsCard } from '../../settings/SettingsCard';
import { SettingsEditDialog } from '../../settings/SettingsEditDialog';
import { SettingsRow } from '../../settings/SettingsRow';
import { SettingsSection } from '../../settings/SettingsSection';

type EditDialog = 'name' | 'companyName' | null;

export type ExternalNetworkGeneralSettingsProps = {
  surface: Pick<ExternalNetworkDto, 'name' | 'organizationName' | 'isEnabled'>;
  isReadOnly: boolean;
  isUpdating: boolean;
  onSave: (dto: UpdateExternalNetworkDto) => Promise<boolean>;
};

export function ExternalNetworkGeneralSettings({
  surface,
  isReadOnly,
  isUpdating,
  onSave,
}: ExternalNetworkGeneralSettingsProps) {
  const t = useTranslations(
    'designSystem.networkSettings.externalSettings.general',
  );
  const tCommon = useTranslations('designSystem.common');
  const [editDialog, setEditDialog] = useState<EditDialog>(null);
  const [name, setName] = useState(surface.name);
  const [companyName, setCompanyName] = useState(
    surface.organizationName ?? '',
  );

  const save = (dto: UpdateExternalNetworkDto) =>
    void onSave(dto).then((ok) => ok && setEditDialog(null));

  const openNameDialog = () => {
    setName(surface.name);
    setEditDialog('name');
  };

  const openCompanyNameDialog = () => {
    setCompanyName(surface.organizationName ?? '');
    setEditDialog('companyName');
  };

  return (
    <VStack alignItems="stretch" gap={8} w="full">
      <SettingsSection title={t('metadata')}>
        <SettingsCard>
          <SettingsRow
            label={t('name')}
            value={surface.name}
            action={{ label: tCommon('edit'), onClick: openNameDialog }}
            isReadOnly={isReadOnly}
          />
          <SettingsRow
            label={t('companyName')}
            value={surface.organizationName ?? t('emptyValue')}
            action={{ label: tCommon('edit'), onClick: openCompanyNameDialog }}
            isReadOnly={isReadOnly}
          />
          <SettingsRow
            label={t('enabled')}
            control={
              <SettingsBooleanControl
                checked={surface.isEnabled ?? false}
                disabled={isUpdating || isReadOnly}
                label={
                  surface.isEnabled ? tCommon('enabled') : tCommon('disabled')
                }
                onCheckedChange={({ checked }) =>
                  void onSave({ isEnabled: checked })
                }
              />
            }
          />
        </SettingsCard>
      </SettingsSection>

      <SettingsEditDialog
        open={editDialog === 'name'}
        title={t('editNameTitle')}
        onOpenChange={(open) => setEditDialog(open ? 'name' : null)}
        onSubmit={() => save({ name: name.trim() })}
        loading={isUpdating}
        isSubmitDisabled={!name.trim()}
      >
        <Field.Root>
          <Field.Label>{t('name')}</Field.Label>
          <Input
            value={name}
            onChange={(event) => setName(event.target.value)}
          />
        </Field.Root>
      </SettingsEditDialog>

      <SettingsEditDialog
        open={editDialog === 'companyName'}
        title={t('editCompanyNameTitle')}
        onOpenChange={(open) => setEditDialog(open ? 'companyName' : null)}
        onSubmit={() => save({ organizationName: companyName.trim() || null })}
        loading={isUpdating}
      >
        <Field.Root>
          <Field.Label>{t('companyName')}</Field.Label>
          <Input
            value={companyName}
            onChange={(event) => setCompanyName(event.target.value)}
          />
        </Field.Root>
      </SettingsEditDialog>
    </VStack>
  );
}
